import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Star, Share2 } from 'lucide-react';
import TopBar from '../components/common/TopBar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { kioskService, productService } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

const KioskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, cart, setCartKiosk, kiosk: cartKiosk, getTotal, getItemCount } = useCart();

  const [kiosk, setKiosk] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('Todos');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [kioskData, productsData] = await Promise.all([
        kioskService.getById(id),
        productService.list(id, null, true),
      ]);
      setKiosk(kioskData);
      setProducts(productsData);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar quiosque');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = (product) => {
    if (cartKiosk && cartKiosk.id !== kiosk.id) {
      if (!window.confirm('Você já tem itens no carrinho de outro quiosque. Deseja limpar e adicionar este produto?')) {
        return;
      }
    }
    setCartKiosk(kiosk);
    addItem(product, 1);
    toast.success(`${product.name} adicionado`);
  };

  const getItemQuantity = (productId) => {
    const item = cart.find((i) => i.product_id === productId);
    return item ? item.quantity : 0;
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/kiosk/${kiosk.id}?utm_source=share`;
    const shareData = {
      title: `${kiosk.display_name} - Beach Pay`,
      text: `🏖️ Confira o cardápio do ${kiosk.display_name} no Beach Pay! Peça direto da areia.`,
      url: shareUrl,
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success('Compartilhado!');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copiado!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Link copiado!');
        } catch {
          // ignore
        }
      }
    }
  };

  if (loading) return <LoadingSpinner size="lg" text="Carregando..." />;
  if (error) return <ErrorMessage message={error} onRetry={loadData} />;
  if (!kiosk) return <ErrorMessage message="Quiosque não encontrado" />;

  const categories = ['Todos', ...new Set(products.map((p) => p.category))];
  const filteredProducts = selectedCategory === 'Todos'
    ? products
    : products.filter((p) => p.category === selectedCategory);

  const cartItemCount = getItemCount();
  const cartTotal = getTotal();

  return (
    <div className="min-h-screen bg-background pb-32" data-testid="kiosk-detail-page">
      <TopBar title={kiosk.display_name} showBack showCart />

      <div className="relative h-48 bg-gradient-to-br from-primary to-secondary">
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative z-10 flex items-center justify-center h-full">
          <span className="text-7xl">🏖️</span>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 -mt-6 relative z-20">
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h1 className="font-['Outfit'] text-xl font-bold text-foreground">
                {kiosk.display_name}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {kiosk.description}
              </p>
            </div>
            {kiosk.is_open && (
              <span className="bg-secondary/20 text-secondary text-xs px-2 py-1 rounded-full font-semibold">
                Aberto
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              Raio: {kiosk.service_radius}m
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              ~20 min
            </span>
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 fill-primary text-primary" />
              4.8
            </span>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              data-testid="share-kiosk"
              onClick={handleShare}
              className="flex-1 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold flex items-center justify-center gap-2 hover:bg-primary/20 transition-colors active:scale-95"
            >
              <Share2 className="w-4 h-4" />
              Compartilhar
            </button>
            <button
              data-testid="kiosk-qr"
              onClick={() => navigate(`/kiosk/${kiosk.id}/qr`)}
              className="flex-1 py-2 bg-secondary/10 text-secondary rounded-full text-sm font-semibold flex items-center justify-center gap-2 hover:bg-secondary/20 transition-colors active:scale-95"
            >
              📱 QR Code
            </button>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-3 mb-3 -mx-4 px-4">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`category-${cat}`}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full font-semibold whitespace-nowrap transition-colors ${
                selectedCategory === cat
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white text-foreground border border-border'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {filteredProducts.map((product) => {
            const quantity = getItemQuantity(product.id);
            return (
              <div
                key={product.id}
                data-testid={`product-${product.id}`}
                className="bg-white rounded-xl shadow-md border border-border overflow-hidden flex"
              >
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-24 h-24 object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-24 h-24 bg-muted flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl">🍹</span>
                  </div>
                )}
                <div className="flex-1 p-3 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between">
                      <h3 className="font-semibold text-foreground">{product.name}</h3>
                      {product.is_promotional && (
                        <span className="bg-accent/20 text-accent text-xs px-2 py-0.5 rounded-full font-semibold">
                          Promo
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                      {product.description}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-bold text-primary">
                      R$ {product.price.toFixed(2)}
                    </span>
                    {quantity === 0 ? (
                      <button
                        data-testid={`add-product-${product.id}`}
                        onClick={() => handleAddItem(product)}
                        className="px-4 py-1.5 bg-primary text-primary-foreground rounded-full text-sm font-semibold hover:opacity-90 transition-opacity active:scale-95"
                      >
                        Adicionar
                      </button>
                    ) : (
                      <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-bold">
                        {quantity}x
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {cartItemCount > 0 && cartKiosk?.id === kiosk.id && (
        <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto p-4 bg-white border-t border-border z-40">
          <button
            data-testid="go-to-cart"
            onClick={() => navigate('/cart')}
            className="w-full py-3 bg-primary text-primary-foreground rounded-full font-semibold flex items-center justify-between px-6 hover:opacity-90 transition-opacity active:scale-95 shadow-lg"
          >
            <span className="bg-white text-primary w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold">
              {cartItemCount}
            </span>
            <span>Ver Carrinho</span>
            <span>R$ {cartTotal.toFixed(2)}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default KioskDetail;
